import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

function LandingPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create email body with form data
    const emailBody = `Hello,

I would like to request a live demo of SVI's Virtual 1004D Inspections.

Contact Information:
- Name: ${formData.firstName} ${formData.lastName}
- Company: ${formData.company}
- Email: ${formData.email}
- Phone: ${formData.phone}

Thank you!`;

    // Encode the email body for mailto link
    const encodedBody = encodeURIComponent(emailBody);
    const encodedSubject = encodeURIComponent('Request for Live Demo - SVI Virtual 1004D Inspections');
    
    // Create mailto link
    const mailtoLink = `mailto:svi-info@stewart.com?subject=${encodedSubject}&body=${encodedBody}`;
    
    // Open email client
    window.location.href = mailtoLink;
  };

  return (
    <div className="landing-page">
      <header>
        <div className="wrap top">
          <img className="logo" src="/svi-logo.png" alt="Stewart Valuation Intelligence"/>
          <nav>
            <a href="https://stewartvaluation.com/why-svi/">Why SVI</a>
            <Link to="/dashboard">Login</Link>
            <a className="btn primary" href="/SVI Virtual 1004D Inspections.pdf" download>Download Fact Sheet</a>
          </nav>
        </div>
      </header>

      <section 
        className="hero"
        style={{
          backgroundImage: `
            linear-gradient(90deg,
              rgba(255, 255, 255, 0.96) 0%,
              rgba(255, 255, 255, 0.92) 40%,
              rgba(255, 255, 255, 0.75) 60%,
              rgba(255, 255, 255, 0.45) 80%,
              rgba(255, 255, 255, 0.20) 100%
            ),
            url(${process.env.PUBLIC_URL}/landing-bg.png)
          `
        }}
      >
        <div className="wrap heroGrid">
          <div>
            <h1>Efficient verification for confident lending decisions.</h1>
            <div className="underline" aria-hidden="true"></div>

            <p className="mainHeader">
              SVI's Virtual 1004D Inspections verify appraisal conditions and required repairs, so lenders can move loans forward with less delay and friction.
            </p>

            <ul className="bullets">
              <li>Live video walkthrough supported by photo documentation.</li>
              <li>Completion-only verification, not a reappraisal.</li>
              <li>Professional review with clear completion status.</li>
            </ul>
          </div>

          <aside className="sticky">
            <div className="stickyHead">Why this works</div>

            <div className="callouts">
              <div className="callout">
                <div className="k">
                  <div className="badge">✓</div>
                  <h3>Scheduling bottleneck removed</h3>
                </div>
                <p>Virtual inspections remove commuting, reduce scheduling friction, and eliminate broad appointment window hassles.</p>
              </div>

              <div className="callout">
                <div className="k">
                  <div className="badge">30K</div>
                  <h3>Panel insight</h3>
                </div>
                <p>Our panel of 30,000+ consistently highlights inspections as a key opportunity to reduce borrower and appraiser friction.</p>
              </div>

              <div className="callout">
                <div className="k">
                  <div className="badge">UX</div>
                  <h3>Lightweight, no downloads</h3>
                </div>
                <p>No apps to install. A simple web link works on iPhone, Android, tablet, or laptop.</p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="flowSection">
        <div className="wrap">
          <div className="flowTop">
            <div>
              <h2 className="flowTitle">How it works</h2>
              <p className="flowSub">A six-step workflow designed to reduce friction while preserving diligence and compliance.</p>
            </div>
          </div>

          <div className="flowRibbon">
            <div className="step">
              <div className="num">01</div>
              <h4>Order is created</h4>
              <p>Order is received by SVI and assigned to an eligible appraiser.</p>
            </div>

            <div className="step">
              <div className="num">02</div>
              <h4>Scheduling</h4>
              <p>Appraiser schedules inspection using SVI's built-in scheduling assistant.</p>
            </div>

            <div className="step">
              <div className="num">03</div>
              <h4>Borrower joins</h4>
              <p>Borrower receives a link and joins the session at inspection time.</p>
            </div>

            <div className="step">
              <div className="num">04</div>
              <h4>Live inspection</h4>
              <p>Live video and audio while the appraiser captures images during the tour.</p>
            </div>

            <div className="step">
              <div className="num">05</div>
              <h4>Submit to SVI</h4>
              <p>Appraiser completes the session and submits the form to SVI.</p>
            </div>

            <div className="step">
              <div className="num">06</div>
              <h4>IQC review</h4>
              <p>Submission passes through SVI's rigorous IQC engine.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="videoSection">
        <div className="wrap">
          <div className="videoContainer">
            <iframe
              src="https://www.youtube.com/embed/CU3qn9Dh6f4"
              title="SVI Virtual 1004D Inspections"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      <section className="lower" id="why">
        <div className="wrap lowerGrid">
          <div>
            <div className="h2">Why SVI</div>
            <div className="tri">
              <p><strong>Accuracy.</strong> Review of live observations, photo documentation, and supporting materials to confirm completion.</p>
              <p><strong>Compliance.</strong> Consistent validation aligned with investor expectations for collateral verification.</p>
              <p><strong>Efficiency.</strong> Live video reduces scheduling and travel delays compared with on-site inspections.</p>
            </div>
          </div>

          <div>
            <div className="h2">Benefits</div>
            <div className="benefits">
              <div>✓ Metadata and GPS verification for auditable records</div>
              <div>✓ Borrowers join by link with no accounts or subscriptions</div>
              <div>✓ Appraisers can revisit prior inspections at any time</div>
              <div>✓ Live video plus photo evidence</div>
              <div>✓ Clear completion summaries and remaining items</div>
            </div>
          </div>
        </div>
      </section>

      <section className="formSection" id="demo">
        <div className="wrap formWrap">
          <div className="formTitle">Request a Live Demo</div>
          <p className="formSub">Tell us a bit about you and we will follow up quickly.</p>

          <form onSubmit={handleSubmit}>
            <div className="formGrid">
              <input
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleInputChange}
              />
              <input
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleInputChange}
              />
              <input
                name="company"
                placeholder="Company"
                className="full"
                value={formData.company}
                onChange={handleInputChange}
              />
              <input
                name="email"
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
              />
              <input
                name="phone"
                type="tel"
                placeholder="Phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>

            <button type="submit" className="submit">Submit Request</button>
            <div className="smallprint">No accounts required. Borrowers join by link, and appraisers can revisit prior inspections at any time.</div>
          </form>
        </div>
      </section>
    </div>
  );
}

export default LandingPage;

